/* eslint-disable no-constant-condition */
import { useTranslation } from 'react-i18next';

// Domain
import { transformFormDataForSubmission } from '../../../domain/form/formUseCase';
import { getLiveSearchFillErrorMessage } from '../../../domain/live-search/liveSearchUseCase';

// Hooks
import useToast from '../../molecules/Toast/useToast';
import { useFormSubmitMutation } from '../../../services/forms/formQueries';

// Components
import Button from '../../atoms/Button/Button';
import FormTabs from '../FormTabs/FormTabs';
import FloatingMenu from '../../atoms/FloatingMenu/FloatingMenu';
import Avatar from '../../atoms/Avatar/Avatar';
import FormContainer from '../../atoms/FormContainer/FormContainer';
import ContactSidebar, { ContactSidebarSection } from '../../molecules/ContactSidebar/ContactSidebar';
import ContactCard, { ContactCardAvatar, ContactCardContent } from '../../molecules/ContactCard/ContactCard';
import ContactAction from '../../atoms/ContactAction/ContactAction';
import MarkdownContainer from '../../atoms/MarkdownContainer/MarkdownContainer';

/**
 * Form panel content organism — presentation and submit; config is loaded by the parent.
 * @param {object} props
 * @param {ApiFormResponse | undefined} props.formConfig - Form configuration
 * @param {boolean} props.isLoading - Whether form config is loading
 * @param {(fieldsToUpdate?: ApiFormFieldsToUpdateItem[]) => void} props.onCloseAfterSuccess - Called after successful submit
 * @param {string} [props.containerId='form-panel'] - Scroll container id for FormTabs
 * @returns {JSX.Element} Form panel content
 */
function FormContent({
  formConfig,
  isLoading,
  onCloseAfterSuccess,
  containerId = 'form-panel',
}) {
  const { t } = useTranslation();
  const { notify } = useToast();

  /**
   * @param {ApiFormPostResponse} data
   */
  const handleFormSubmitSuccess = (data) => {
    const message = t('toast.formSubmit.success', 'Formulaire soumis avec succès');
    notify({
      message,
      type: 'success',
    });
    onCloseAfterSuccess(data?.fieldsToUpdate ?? []);
  };

  /**
   * @param {any} error
   */
  const handleFormSubmitError = (error) => {
    const message = error?.message || t('toast.formSubmit.error', 'Une erreur est survenue lors de la soumission');
    notify({
      message,
      type: 'alert',
    });
  };

  /**
   * @param {unknown} error
   */
  const handleLiveSearchFillError = (error) => {
    const fillMessage = getLiveSearchFillErrorMessage(error);
    const message = fillMessage || t('toast.formSubmit.error', 'Une erreur est survenue lors de la soumission');
    notify({
      message,
      type: 'alert',
    });
  };

  // Mutation - Form Submission
  const { mutate: mutateFormSubmit, isPending: isFormSubmitPending } = useFormSubmitMutation({
    onSuccess: handleFormSubmitSuccess,
    onError: handleFormSubmitError,
  });

  /**
   * POST sanitized form data to the given submit URL.
   * @param {string} submitUrl
   * @param {Record<string, unknown>} sanitizedData
   * @param {ApiFormSubmissionField[]} submissionParams
   */
  const submitFormData = (submitUrl, sanitizedData, submissionParams) => {
    if (!formConfig) {
      notify({
        message: t('toast.formSubmit.noConfig', 'Configuration du formulaire non disponible'),
        type: 'alert',
      });
      return;
    }

    if (!submitUrl) {
      notify({
        message: t('toast.formSubmit.noUrl', 'URL de soumission non disponible'),
        type: 'alert',
      });
      return;
    }

    const transformedData = transformFormDataForSubmission(
      sanitizedData,
      formConfig,
      submissionParams,
    );

    mutateFormSubmit({
      sendFormUrl: submitUrl,
      transformedData,
    });
  };

  /**
   * Handle manual form submission (floating action button).
   * @param {Record<string, unknown>} sanitizedData
   * @param {string} submitUrl - Resolved send URL (may be overridden by live search editSendFormUrl)
   * @param {ApiFormSubmissionField[]} submissionParams - Resolved params (may be overridden by livesearch-fill)
   */
  const handleFormSubmit = (sanitizedData, submitUrl, submissionParams) => {
    submitFormData(submitUrl, sanitizedData, submissionParams);
  };

  const mockAvatar = 'https://placehold.co/400';
  const mockMarkdown = '**Test Test**\n\nDirecteur\n\ntest.test@zol.fr\n\n06 00 00 00 00\n\n06 00 00 00 00';
  return (
    <FormContainer>
      {false ? (
        <ContactSidebar>
          <ContactSidebarSection>
            <Button color="no-background" size="small" icon="chevron-left">
              {t('form.backToList')}
            </Button>
            <ContactCard>
              <ContactCardAvatar src={mockAvatar} alt="test" />
              <ContactCardContent>
                <MarkdownContainer content={mockMarkdown} />
              </ContactCardContent>
            </ContactCard>
          </ContactSidebarSection>
          <ContactSidebarSection>
            <ContactAction>
              <Avatar src={mockAvatar} alt="test" />
              SARL Intrazik
            </ContactAction>
          </ContactSidebarSection>
        </ContactSidebar>
      ) : null}
      <FormTabs
        formConfig={formConfig}
        onSubmit={handleFormSubmit}
        onLiveSearchAutoSubmitSuccess={handleFormSubmitSuccess}
        onLiveSearchAutoSubmitError={handleFormSubmitError}
        onLiveSearchFillError={handleLiveSearchFillError}
        options={{ mode: 'onBlur' }}
        containerId={containerId}
        isLoading={isLoading}
      >
        {(isSubmitting) => (
          <FloatingMenu>
            <Button
              isSubmit
              color="primary"
              isLoading={isSubmitting || isFormSubmitPending}
              disabled={isSubmitting}
              size="small"
            >
              {isSubmitting ? t('form.submitting') : formConfig?.sendFormLib}
            </Button>
          </FloatingMenu>
        )}
      </FormTabs>
    </FormContainer>
  );
}

export default FormContent;
